/**
 * Milestone list arithmetic and field coercion.
 *
 * Separate from `MilestoneList.tsx` so that file exports only a component
 * (fast refresh), and so the ordering and grouping are testable without
 * rendering.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.2, PRD §5.4
 */
import type { PilotMilestone } from '@nexus/contracts';

import {
  MILESTONE_STATUSES,
  type MilestoneStatus,
} from '../../../components/domain/pilot-status';

/** A row being edited. */
export interface MilestoneDraft {
  key: string;
  description: string;
  dueDate: string;
  status: MilestoneStatus;
}

/** Turns a saved milestone into an editable draft. */
export function toMilestoneDraft(milestone: PilotMilestone): MilestoneDraft {
  return {
    key: milestone.id,
    description: milestone.description,
    /* Sliced to `yyyy-mm-dd` for a native date input, which rejects a full
       ISO timestamp and silently renders blank rather than erroring. */
    dueDate: milestone.dueDate === null ? '' : milestone.dueDate.slice(0, 10),
    status: milestone.status,
  };
}

/** An empty row, for the add button. */
export function emptyMilestoneDraft(): MilestoneDraft {
  return {
    key: crypto.randomUUID(),
    description: '',
    dueDate: '',
    status: 'NOT_STARTED',
  };
}

/**
 * Turns the drafts into the API's payload.
 *
 * Rows with no description are dropped rather than rejected: an empty row is
 * what the Add button produces, and someone who adds one and changes their
 * mind should not have to remove it before the list will save.
 *
 * A date-only value is widened to midnight UTC. The API expects an ISO-8601
 * timestamp, and `new Date('2026-05-01')` is already UTC midnight — but
 * building the string explicitly means the payload does not depend on the
 * browser's timezone, which would shift a due date by a day either side of
 * the meridian.
 */
export function milestoneDraftsToPayload(drafts: readonly MilestoneDraft[]): {
  description: string;
  dueDate?: string;
  status: MilestoneStatus;
}[] {
  return drafts
    .filter((draft) => draft.description.trim() !== '')
    .map((draft) => ({
      description: draft.description.trim(),
      ...(draft.dueDate === '' ? {} : { dueDate: `${draft.dueDate}T00:00:00.000Z` }),
      status: draft.status,
    }));
}

/**
 * Whether a milestone is late.
 *
 * Past its due date and not done. A done milestone that was completed late is
 * history — the completion date records that — while an overdue open one is a
 * task, and mixing them would bury what still needs doing.
 *
 * `SLIPPED` is already an explicit admission, so it is not additionally
 * flagged: the team has said so, and a second marker adds nothing.
 */
export function isMilestoneLate(
  milestone: Pick<PilotMilestone, 'dueDate' | 'status'>,
  now: Date = new Date(),
): boolean {
  if (milestone.dueDate === null) return false;
  if (milestone.status === 'DONE' || milestone.status === 'SLIPPED') return false;

  return new Date(milestone.dueDate).getTime() < now.getTime();
}

/** How many milestones are done, for the card's count. */
export function countDone(milestones: readonly PilotMilestone[]): number {
  return milestones.filter((milestone) => milestone.status === 'DONE').length;
}

/**
 * Binds the status Select for one row.
 *
 * Named rather than written inline because a Radix Select cannot be driven to
 * invoke its handler in jsdom — it positions its viewport with
 * `document.elementFromPoint`, which jsdom does not implement. As a named
 * function the binding is testable for what it is.
 *
 * A value that is not a milestone status is ignored rather than written: the
 * options come from our own list, so this only fires if something else writes
 * to the control.
 */
export function statusChangeHandler(
  apply: (status: MilestoneStatus) => void,
): (value: string) => void {
  return (value: string): void => {
    if ((MILESTONE_STATUSES as readonly string[]).includes(value)) {
      apply(value as MilestoneStatus);
    }
  };
}
